import {
  AccessRequestStatus,
  AccessReviewStatus,
  CloudDeployment,
  ContactData,
} from './global.objects';
import {
  AccessRequestBase,
  AccessRequestBulkUpdateResponseBase,
  AccessRequestBulkUpdateResultBase,
} from './access-management.objects';

/* CLASSES */

/**
 * Error thrown when a requested agent cannot be found
 */
export class AgentNotFoundError extends Error {
  constructor(id: string) {
    super(`Agent with id '${id}' not found`);
    this.name = 'AgentNotFoundError';
  }
}

/* ENUMS */

/**
 * Account types for request access flow
 */
export enum AccountType {
  IndividualUser = 'individualUser',
  ServicePrincipal = 'servicePrincipal',
  TechnicalAccount = 'technicalAccount',
}

/**
 * Agent categories supported in the agent garden.
 */
export enum AgentCategory {
  Enterprise = 'Enterprise',
  Universal = 'Universal',
}

/**
 * Agent protocols supported in the agent garden
 */
export enum AgentProtocol {
  A2A = 'A2A',
  MCP = 'MCP',
}

/**
 * Agent services supported in the agent garden.
 */
export const AgentService = {
  Enterprise: [
    'Databricks',
    'Vertex AI',
    'Cloud Run',
    'Microsoft Foundry',
    'n8n',
    'Others',
  ],
  Universal: [
    'Langchain',
    'Langchain with Langraph',
    'AgentFlow',
    'AutoGen',
    'CrewAI',
    'LlamaIndex',
    'Microsoft Semantic Kernel',
    'Others',
  ],
  GCP: ['Vertex AI', 'Cloud Run'],
  Databricks: 'Databricks',
  VertexAI: 'Vertex AI',
  CloudRun: 'Cloud Run',
  MicrosoftFoundry: 'Microsoft Foundry',
  N8n: 'n8n',
  Langchain: 'Langchain',
  LangchainWithLangraph: 'Langchain with Langraph',
  AgentFlow: 'AgentFlow',
  AutoGen: 'AutoGen',
  CrewAI: 'CrewAI',
  LlamaIndex: 'LlamaIndex',
  MicrosoftSemanticKernel: 'Microsoft Semantic Kernel',
  Others: 'Others',
} as const;

/**
 * Agent status types for agent garden and agent details page
 */
export enum AgentStatus {
  Development = 'development',
  Production = 'production',
}

/**
 * Info classification types for agent details page
 */
export enum InfoClassification {
  Internal = 'Internal',
  Confidential = 'Confidential',
  Secret = 'Secret',
  Public = 'Public',
}

/**
 * Task action type for agent details page
 */
export enum TaskAction {
  Chat = 'Chat',
  Completion = 'Completion',
  Embeddings = 'Embeddings',
}

// ---------------------------------------------------------------------------------------------------- //
// Types
// ---------------------------------------------------------------------------------------------------- //

/**
 * Object containing all Agent Access Request Status values
 */
export const AgentAccessRequestStatus = {
  ...AccessRequestStatus,
  Retained: 'Retained',
  Ongoing: 'Ongoing',
} as const;

/**
 * Type for Agent Access Request Status
 */
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional const+type declaration merge
export type AgentAccessRequestStatus =
  | AccessRequestStatus
  | 'Retained'
  | 'Ongoing';

/**
 * Type for Agent Access Review Status
 */
export type AgentAccessReviewStatus = AccessReviewStatus;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional const+type declaration merge
export const AgentAccessReviewStatus = AccessReviewStatus;

// ---------------------------------------------------------------------------------------------------- //
// Interfaces
// ---------------------------------------------------------------------------------------------------- //

/**
 * Interface for Agent Data Detailed object type, used in agent details page
 */
export interface AgentDataDetailed {
  agentId: string;
  agentName: string;
  agentService: string;
  overview: string;
  additionalDetails: string;
  businessDomain: string;
  userRateLimit: string;
  endpointRateLimit: string;
  leanixAppId: string;
  massp4aiUseCaseId?: string;
  endpointUrl: string;
  documentationUrl: string;
  accessRequestId: string;
  deprecationDate: string;
  countries: string[];
  isProvider: boolean;
  acceptTerms: boolean;
  toBeDeprecated: boolean;
  canEdit: boolean;
  canDeprecate: boolean;
  deprecationDurationDays: number;
  activeConsumersCount: number;
  cloudDeployments: CloudDeployment[];
  status: AgentStatus;
  infoClassification: InfoClassification;
  l3Manager: ContactData;
  informationOwner: ContactData;
  aiProductOwner: ContactData;
  technicalContacts: ContactData[];
  accessRequestStatus: AgentAccessRequestStatus;
  agentCategory: AgentCategory;
  onboardingStatus: string;

  hasCustomLogo?: boolean;
  customLogoPresignedUrl?: string;
  agentFramework?: string;
  providerNpmMailbox?: string;
  repoUrl?: string;
  agentUrl?: string;
  agentProtocol?: AgentProtocol;
  tokenUrl?: string;
  oauthScope?: string;
  apiKeyLocation?: string;
  apiKeyLocationName?: string;
  apiKeyExpiresAt?: string;
  nextAccessReviewAtDate?: string;
  agentAccessReviewStatus?: AgentAccessReviewStatus;
  providerAccessReviewStatus?: AgentAccessReviewStatus;
}

/**
 * Interface for a MASSP4AI AI use case linked to an agent. The data is read live from MASSP4AI and read-only in Agent Garden.
 */
export interface Massp4aiUseCase {
  id: string;
  name?: string;
  shortDescription?: string;
  expectedCompanyBenefit?: string;
  expectedCustomerBenefit?: string;
  businessNeed?: string;
  mitigationMeasuresScanStatus?: string;
}

/**
 * Interface for Agent Data Reduced type, used in agent garden page
 */
export interface AgentDataReduced {
  agentId: string;
  agentName: string;
  overview: string;
  additionalDetails: string;
  agentService: string;
  pendingAccessRequestsCount: number;
  isProvider: boolean;
  toBeDeprecated: boolean;
  canEdit: boolean;
  canDeprecate: boolean;
  status: AgentStatus;
  agentCategory: AgentCategory;

  customLogoPresignedUrl?: string; // universal agents
  hasCustomLogo?: boolean; // universal agents
  releaseDate?: string;
  agentFramework?: string; // universal agents
  businessDomain?: string;
  agentProtocol?: AgentProtocol;
  infoClassification?: InfoClassification;
  agentAccessReviewStatus?: AgentAccessReviewStatus;
}

/**
 * Interface for Agent List Response, used in agent garden page
 */
export interface AgentListResponse {
  agents: AgentDataReduced[];
  lastItemKey?: string;
  canCreateUniversalAgents?: boolean;
  isDevModeAuthorized?: boolean;
}

/**
 * Interface for Agent Access Requests, used in agent details page
 */
export interface AgentAccessRequest extends AccessRequestBase {
  status: AgentAccessRequestStatus;
  accountType: AccountType;
  requesterShortId: string;
  requesterName: string;
  requesterEmail: string;
  businessRegion: string;
  userPrincipalName: string;
  businessNeed: string;

  applicationId?: string;
  consumptionEstimate?: string;
  decidedAtUTC?: string;
  decisionNotes?: string;
  clientId?: string;
  consumerAccessReviewStatus?: AgentAccessReviewStatus;
}

/**
 * Shape of a single supported endpoint path exposed via the Agent Gateway.
 */
export interface SupportedEndpointPath {
  method: string;
  path: string;
  description: string;
}

/**
 * Interface for Agent Endpoint Data, used in agent garden and agent details page
 */
export interface AgentEndpointData {
  endpointUrl: string;
  agentServiceEndpointUrl: string; // user-faced endpointUrl
  userRateLimit: string;
  endpointRateLimit: string;
  agentService: string;
  task?: string; // dbx
  gcpProjectId?: string | null; // gcp
  customAuthAudience?: string | null; // cloudrun
  supportedEndpointPaths?: SupportedEndpointPath[] | null;
  baseUrl?: string; // n8n
  requestFormat?: string; // n8n
  responseFormat?: string; // n8n
  hasMemorySupport?: boolean; // n8n
  sessionIdField?: string; // n8n
  headerAuthKeyName?: string; // n8n
  headerAuthSecretValue?: string; // n8n
  apiKey?: string; // n8n
  apiKeyExpiresAt?: string; // n8n
  authType?: string; // a2a/mcp
  tokenUrl?: string; // a2a/mcp
  oauthScope?: string; // a2a/mcp
  apiKeyLocation?: string; // a2a/mcp
  apiKeyLocationName?: string; // a2a/mcp
}

/**
 * Interface for Agent Provider, used in agent details page
 */
export interface AgentProvider {
  role: string;
  shortId: string;
  email: string;
}

/**
 * Result of an updated agent access request
 */
export interface AgentAccessUpdateResult extends AccessRequestBulkUpdateResultBase {
  consumerAccessReviewStatus?: string;
  consumerAccessReviewedAtUTC?: string;
  consumerAccessReviewedBy?: string;
  error?: string;
  errorStatusCode?: number;
}

/**
 * Response from updating agent access requests
 */
export interface AgentAccessUpdateResponse extends AccessRequestBulkUpdateResponseBase {
  results: AgentAccessUpdateResult[];
}
